<script>
import { preventDefault } from 'svelte/legacy';
import { toast } from "@zerodevx/svelte-toast";
import { flip } from "svelte/animate";
import { dndzone } from "svelte-dnd-action";
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    metadataState
} from '@app/store/metadataStore.svelte.js';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import { activateSubscription } from "@app/lib/activateSubscription.js";
import tcLib from '@cclib/lib/timecode.js';
import offset from "@cclib/functions/events/tcOffset.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import {
    saveAs
} from 'file-saver';
import * as messages from "@app/paraglide/messages.js";
import {
    buildTranscriptExport,
    normalizeTranscriptExportConfig,
    toPresetConfig,
    loadSavedTranscriptExportOptions,
    saveTranscriptExportOptions,
    isFieldEnabled,
    PROSE_FIELD_IDS,
    LABELED_FIELD_IDS,
} from "@app/lib/transcriptExport/index.js";
import { hasOriginalText } from "@app/lib/eventGroupTypes.js";

/* Export Preset Store */
import {
    createPreset,
    getPresetsByType,
    getPresetById,
    PRESET_TYPES
} from "@app/store/presetStore.svelte.js";

const m = messages.m ?? messages;
const flipDurationMs = 180;

/** Call a paraglide message, with fallback when HMR has a stale messages module. */
function msg(fn, fallback, inputs) {
    return typeof fn === "function" ? (inputs !== undefined ? fn(inputs) : fn()) : fallback;
}

const projectTcOffset = tcLib.secToTc(projectState.incode, projectState.frameRate, projectState.dropFrame) || "00:00:00:00";
const defaults = loadSavedTranscriptExportOptions({ tcOffset: projectTcOffset });

let eventGroup = $state(getSelectedEventGroup(["subtitle", "translation", "audio description", "audio dubbing", "transcription"])),
    layout = $state(defaults.layout),
    fileFormat = $state(defaults.fileFormat),
    exportMetadata = $state(defaults.exportMetadata),
    preserveLineBreaks = $state(defaults.preserveLineBreaks),
    fieldLayout = $state(defaults.fieldLayout.map((f) => ({ ...f }))),
    tcFormat = $state(defaults.tcFormat),
    autoGroupByTc = $state(defaults.autoGroupByTc),
    duration = $state(defaults.duration),
    showOptions = $state(false),
    tcOffset = $state(projectTcOffset),
    tcOffsetType = $state(defaults.tcOffsetType),
    availablePresets = $derived(getPresetsByType(PRESET_TYPES.TRANSCRIPT)),
    selectedPresetId = $state(null);

let showTimesFormat = $derived(
    isFieldEnabled(fieldLayout, 'start') || isFieldEnabled(fieldLayout, 'end')
);

/** Whether a field row is applicable for the current layout / event group. */
function isFieldApplicable(fieldId) {
    if (fieldId === 'original' && !(eventGroup && hasOriginalText(eventGroup.type))) {
        return false;
    }
    if (layout === 'prose' && fileFormat !== 'csv' && !PROSE_FIELD_IDS.has(fieldId)) {
        return false;
    }
    return true;
}

if (authState.status === "in_trial"){
    showTrialWarning();
}

function validateTc(e) {
    tcOffset = tcLib.tcValidate(e.target.value, parseFloat(projectState.frameRate), projectState.dropFrame);
}

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup){
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup;
        } else {
            return false;
        }
    } else {
        return false;
    }    
}

function fieldLabel(id) {
    switch (id) {
        case 'index': return msg(m.transcript_export_field_index, 'Number ID');
        case 'start': return msg(m.transcript_export_field_start, 'Start Time');
        case 'end': return msg(m.transcript_export_field_end, 'End Time');
        case 'speakers': return msg(m.transcript_export_field_speakers, 'Speakers');
        case 'text': return msg(m.transcript_export_field_text, 'Text');
        case 'original': return msg(m.transcript_export_field_original, 'Original');
        case 'tags': return msg(m.transcript_export_field_tags, 'Tags');
        case 'notes': return msg(m.transcript_export_field_notes, 'Notes');
        case 'notesCheck': return msg(m.transcript_export_field_notes_check, 'Notes Checked');
        case 'reply': return msg(m.transcript_export_field_reply, 'Reply');
        case 'replyCheck': return msg(m.transcript_export_field_reply_check, 'Reply Checked');
        case 'approved': return msg(m.transcript_export_field_approved, 'Approved');
        case 'forced': return msg(m.transcript_export_field_forced, 'Forced');
        default: return id;
    }
}

function getCurrentOptions() {
    return {
        layout,
        fileFormat,
        exportMetadata,
        preserveLineBreaks,
        tcFormat,
        autoGroupByTc,
        duration,
        tcOffset,
        tcOffsetType,
        fieldLayout: fieldLayout.map((f) => ({ ...f })),
    };
}

function applyOptions(options) {
    layout = options.layout;
    fileFormat = options.fileFormat;
    exportMetadata = options.exportMetadata;
    preserveLineBreaks = options.preserveLineBreaks;
    tcFormat = options.tcFormat;
    autoGroupByTc = options.autoGroupByTc;
    duration = options.duration;
    if (options.tcOffset) tcOffset = options.tcOffset;
    tcOffsetType = options.tcOffsetType;
    fieldLayout = (options.fieldLayout || defaults.fieldLayout).map((f) => ({ ...f }));
}

function updateField(id, patch) {
    fieldLayout = fieldLayout.map((field) => {
        if (field.id !== id) return field;
        const next = { ...field, ...patch };
        if (id === 'text') next.enabled = true;
        return next;
    });
}

function nextBreakAfter(breakAfter) {
    return ((Number(breakAfter) || 0) + 1) % 3;
}

function breakAfterTitle(breakAfter) {
    if (breakAfter === 2) return msg(m.transcript_export_break_double, 'Blank line after');
    return msg(m.transcript_export_break_after, 'New line after');
}

/**
 * @param {CustomEvent} e
 */
function handleFieldDnd(e) {
    fieldLayout = e.detail.items.map((item) => ({ ...item }));
}

function showTrialWarning(){
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.transcript_export_trial_title(),
        text : m.transcript_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.transcript_export_trial_activate(),
        denyButtonText: m.transcript_export_trial_continue(),
        allowOutsideClick : false,
        allowEscapeKey : false,
        customClass: SWAL_CLASSES.trial,
    }).then((result) => {
        if (result.isConfirmed) {
            activateSubscription();
        }
    }).catch(err =>{
        console.log(err);
        console.log(err.message)
    });
}

async function saveAsPreset() {
    const { value: presetName } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.transcript_export_preset_save_title(),
        input: 'text',
        inputLabel: m.transcript_export_preset_name_label(),
        inputPlaceholder: m.transcript_export_preset_name_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.transcript_export_preset_save_confirm(),
        cancelButtonText: m.transcript_export_preset_save_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.transcript_export_preset_name_required();
            }
        }
    });

    if (presetName) {
        try {
            const config = toPresetConfig(getCurrentOptions());

            // The preset store resolves the Firebase auth uid itself so that
            // saves land in the same collection loadPresets reads from.
            const exportPreset = await createPreset({
                name: presetName,
                type: PRESET_TYPES.TRANSCRIPT,
                config
            });

            toast.push(m.transcript_export_preset_saved({ name: presetName }), { classes: ['toast-success'] });
            selectedPresetId = exportPreset.id;
        } catch (error) {
            console.error("Error saving preset:", error);
            toast.push(m.transcript_export_preset_save_failed({ error: error.message }), { classes: ['toast-danger'] });
        }
    }
}

async function showLoadPresetDialog() {
    if (availablePresets.length === 0) {
        await Swal.fire({
            ...SWAL_BASE,
            title: m.transcript_export_presets_none_title(),
            text: m.transcript_export_presets_none_text(),
            icon: 'info',
            confirmButtonText: m.ok(),
            customClass: SWAL_CLASSES.ok,
        });
        return;
    }

    const presetOptions = {};
    availablePresets.forEach(presetItem => {
        presetOptions[presetItem.id] = presetItem.name;
    });

    const result = await Swal.fire({
        ...SWAL_BASE,
        title: m.transcript_export_preset_load_title(),
        input: 'select',
        inputOptions: presetOptions,
        inputPlaceholder: m.transcript_export_preset_load_placeholder(),
        showCancelButton: true,
        confirmButtonText: m.transcript_export_preset_load_confirm(),
        cancelButtonText: m.transcript_export_preset_load_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        inputValidator: (value) => {
            if (!value) {
                return m.transcript_export_preset_load_required();
            }
        }
    });

    if (result.isConfirmed && result.value) {
        selectedPresetId = result.value;
        loadPreset(result.value);
    }
}

function loadPreset(presetId) {
    if (!presetId) return;

    const exportPreset = getPresetById(presetId);
    if (!exportPreset || !exportPreset.config) {
        toast.push(m.transcript_export_preset_not_found(), { classes: ['toast-warning'] });
        return;
    }

    applyOptions(normalizeTranscriptExportConfig(exportPreset.config, {
        tcOffset: tcOffset,
    }));
    
    toast.push(m.transcript_export_preset_loaded({ name: exportPreset.name }), { classes: ['toast-success'] });
}

function getExportLabels() {
    return {
        yes: msg(m.yes, "Yes"),
        no: msg(m.no, "No"),
        approved: msg(m.editor_filter_approved, "Approved"),
        failed: msg(m.editor_filter_failed, "Failed"),
        needsReview: msg(m.editor_filter_needs_review, "Needs Review"),
        notes: msg(m.transcript_export_field_notes, "Notes"),
        notesCheck: msg(m.transcript_export_field_notes_check, "Notes Checked"),
        reply: msg(m.transcript_export_field_reply, "Reply"),
        replyCheck: msg(m.transcript_export_field_reply_check, "Reply Checked"),
        approvedLabel: msg(m.transcript_export_field_approved, "Approved"),
        forced: msg(m.transcript_export_field_forced, "Forced"),
        tags: msg(m.transcript_export_field_tags, "Tags"),
        original: msg(m.transcript_export_field_original, "Original"),
        metadataHeader: msg(m.transcript_export_metadata_header, "****METADATA****"),
        metadataNone: msg(m.transcript_export_metadata_none, "No metadata found"),
    };
}

async function exportTranscription() {
    if (!eventGroup) return;

    let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroup));
    if (authState.status === "in_trial"){
        copyOfEventGroup = replaceTrialText(copyOfEventGroup);
    }

    if (tcOffset && tcOffset !== "00:00:00:00") {
        copyOfEventGroup.events.forEach((event, index, events) => {
            events[index] = offset(event, tcLib.tcToSec(tcOffset, projectState.frameRate, projectState.dropFrame), tcOffsetType);
        });
    }

    try {
        const { blob, filename, extension } = await buildTranscriptExport(
            copyOfEventGroup,
            getCurrentOptions(),
            {
                project: projectState,
                metadata: metadataState,
                labels: getExportLabels(),
            }
        );

        saveAs(blob, filename, extension === 'docx' ? undefined : { autoBom: true });
        saveTranscriptExportOptions(getCurrentOptions());
        hideModal();
    } catch (error) {
        console.error("Transcript export failed:", error);
        toast.push(error.message || String(error), { classes: ['toast-danger'] });
    }
}
</script>

<div 
    transition:fade={{duration: 100}} 
    class="modal {modalState.selected === 'transcriptExport' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="transcriptExportTitle"
    aria-describedby="transcriptExportDesc"
    tabindex="-1" 
    id="TranscriptExportModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="transcriptExportTitle">{m.transcript_export_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.transcript_export_close_label()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p class="small text-muted" id="transcriptExportDesc"><i class="bi bi-lightning-fill" aria-hidden="true"></i> {m.transcript_export_powered_by()}</p>
                <form onsubmit={preventDefault(exportTranscription)} aria-label={m.transcript_export_form_label()}>
                    <div class="mb-3">
                        <label class="form-label" for="eventGroupSelect">{m.transcript_export_event_group_label()}</label>
                        <select class="form-select" id="eventGroupSelect" bind:value={eventGroup} aria-required="true">
                            {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                            <option value={eventGroupOption} selected={eventGroup.id === eventGroupOption.id}>{eventGroupOption.name}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="layoutSelect">{msg(m.transcript_export_layout_label, 'Layout')}</label>
                        <select class="form-select" id="layoutSelect" bind:value={layout} aria-required="true" disabled={fileFormat === 'csv'}>
                            <option value="cued">{msg(m.transcript_export_layout_cued, 'Cued (one entry per event)')}</option>
                            <option value="prose">{msg(m.transcript_export_layout_prose, 'Prose (grouped by silence)')}</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="formatSelect">{m.transcript_export_file_format_label()}</label>
                        <select class="form-select" id="formatSelect" bind:value={fileFormat} aria-required="true">
                            <option value="docx">.docx</option>
                            <option value="txt">.txt</option>
                            <option value="csv">.csv</option>
                        </select>
                    </div>
                    <button type="button" 
                        id="moreOptionsBtn"
                        class="btn btn-outline-warning mb-2 dropdown-toggle" 
                        onclick={()=> {showOptions = !showOptions}}
                        aria-expanded={showOptions}
                        aria-controls="advancedOptions">
                        {m.transcript_export_more_options()}
                    </button>
                    {#if showOptions}
                    <div id="advancedOptions" class="p-3 bg-secondary shadow rounded">
                        <div class="row">
                            <p class="text-warning mb-1" id="tcOptionsLabel">{m.transcript_export_timecode_options_label()}</p>
                            <div class="mb-3 col-8">
                                <label class="form-label" for="tcOffset">{m.transcript_export_tc_offset_label()}</label>
                                <input type="text" 
                                    class="form-control" 
                                    id="tcOffset"
                                    bind:value={tcOffset} 
                                    onblur={validateTc} 
                                    onfocus={(e)=>{e.target.select()}}
                                    aria-describedby="tcOffsetHelp"/>
                            </div>

                            <div class="mb-3 col-4">
                                <label class="form-label" for="tcOffsetType">{m.transcript_export_offset_type_label()}</label>
                                <select class="form-select" id="tcOffsetType" bind:value={tcOffsetType}>
                                    <option value="add">{m.transcript_export_offset_add()}</option>
                                    <option value="subtract">{m.transcript_export_offset_subtract()}</option>
                                </select>
                            </div>
                        </div>
                        {#if layout === 'prose' && fileFormat !== 'csv'}
                        <div class="row">
                            <p class="text-warning mb-1" id="groupingOptionsLabel">{m.transcript_export_grouping_options_label()}</p>
                            <div class="mb-3 col-4">
                                <div class="form-check">
                                    <input class="form-check-input" 
                                        type="checkbox" 
                                        bind:checked={autoGroupByTc} 
                                        id="groupEventsByTcCheck"
                                        aria-describedby="groupEventsHelp">
                                    <label class="form-check-label" for="groupEventsByTcCheck">
                                        {m.transcript_export_group_by_timecode_label()}
                                    </label>
                                </div>
                            </div>
                            {#if autoGroupByTc}
                            <div class="mb-3 col">
                                <label class="form-label" for="durationControlRange">
                                    {m.transcript_export_duration_label()} <span class="visually-hidden">{m.transcript_export_duration_seconds()}</span>
                                    <i class="bi bi-info-circle" aria-hidden="true"></i> 
                                    <small>{m.transcript_export_duration_value({ duration })}</small>
                                </label>
                                <input type="range" 
                                    min="0" 
                                    max="100" 
                                    step="0.5" 
                                    id="durationControlRange" 
                                    bind:value={duration}
                                    aria-describedby="durationHelp">
                                <p id="durationHelp" class="visually-hidden">{m.transcript_export_duration_help()}</p>
                            </div>
                            {/if}
                        </div>
                        <hr/>
                        {/if}
                        {#if showTimesFormat}
                        <div class="mb-3">
                            <label class="form-label" for="tcFormat">{m.transcript_export_timecode_format_label()}</label>
                            <select class="form-select" id="tcFormat" bind:value={tcFormat}>
                                <option value="smpte">{m.transcript_export_timecode_format_smpte()}</option>
                                <option value="ms">{m.transcript_export_timecode_format_ms()}</option>
                                <option value="seconds">{m.transcript_export_timecode_format_seconds()}</option>
                            </select>
                        </div>
                        <hr/>
                        {/if}
                        <div class="mb-3">
                            {#if fileFormat !== 'csv'}
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" bind:checked={exportMetadata} id="exportMetdataOption">
                                <label class="form-check-label" for="exportMetdataOption">
                                    {m.transcript_export_include_metadata()}
                                </label>
                            </div>
                            {/if}
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" bind:checked={preserveLineBreaks} id="preserveLineBreaksOption">
                                <label class="form-check-label" for="preserveLineBreaksOption">
                                    {msg(m.transcript_export_preserve_line_breaks, 'Keep line breaks in text')}
                                </label>
                            </div>
                        </div>
                        <hr/>
                        <p class="text-warning mb-1">{msg(m.transcript_export_field_layout_label, 'Field Layout')}</p>
                        <p class="small text-muted mb-2">{msg(m.transcript_export_field_layout_hint, 'Drag to reorder. Wrapping applies to all formats. Label prefix and line breaks (click again for a blank line) apply to TXT/DOCX; bold/italic are used in DOCX only.')}</p>
                        <div
                            class="field-layout-list"
                            use:dndzone={{ items: fieldLayout, flipDurationMs, dropTargetStyle: {}, type: 'transcript-field-layout' }}
                            onconsider={handleFieldDnd}
                            onfinalize={handleFieldDnd}>
                            {#each fieldLayout as field (field.id)}
                            <div
                                class="field-layout-row"
                                class:field-layout-row-muted={!isFieldApplicable(field.id)}
                                animate:flip={{ duration: flipDurationMs }}>
                                <span
                                    class="drag-handle"
                                    title={msg(m.transcript_export_drag_handle, 'Drag to reorder')}
                                    aria-hidden="true">
                                    <i class="bi bi-grip-vertical"></i>
                                </span>
                                <input
                                    type="checkbox"
                                    class="form-check-input"
                                    checked={field.enabled}
                                    disabled={field.id === 'text' || !isFieldApplicable(field.id)}
                                    onchange={(e) => updateField(field.id, { enabled: e.currentTarget.checked })}
                                    aria-label={fieldLabel(field.id)} />
                                <span class="field-label small text-truncate">{fieldLabel(field.id)}</span>
                                <div class="field-controls ms-auto">
                                    <select
                                        class="form-select form-select-sm py-0 wrap-select"
                                        title={msg(m.transcript_export_wrap_label, 'Wrap value')}
                                        aria-label={msg(m.transcript_export_wrap_label, 'Wrap value')}
                                        value={field.wrap}
                                        disabled={!isFieldApplicable(field.id)}
                                        onchange={(e) => updateField(field.id, { wrap: e.currentTarget.value })}>
                                        <option value="none">{msg(m.transcript_export_wrap_none, 'No wrap')}</option>
                                        <option value="square">[ ]</option>
                                        <option value="round">( )</option>
                                        <option value="curly">{'{ }'}</option>
                                    </select>
                                    <button
                                        type="button"
                                        class="btn btn-sm py-0 px-1 {field.showLabel && LABELED_FIELD_IDS.has(field.id) ? 'btn-warning' : 'btn-outline-secondary'}"
                                        title={msg(m.transcript_export_show_label, 'Show label prefix')}
                                        aria-label={msg(m.transcript_export_show_label, 'Show label prefix')}
                                        aria-pressed={field.showLabel && LABELED_FIELD_IDS.has(field.id)}
                                        disabled={!LABELED_FIELD_IDS.has(field.id) || !isFieldApplicable(field.id) || fileFormat === 'csv'}
                                        onclick={() => updateField(field.id, { showLabel: !field.showLabel })}>
                                        Aa:
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm py-0 px-1 break-btn {field.breakAfter ? 'btn-warning' : 'btn-outline-secondary'}"
                                        title={breakAfterTitle(field.breakAfter)}
                                        aria-label={breakAfterTitle(field.breakAfter)}
                                        aria-pressed={field.breakAfter > 0}
                                        disabled={!isFieldApplicable(field.id) || fileFormat === 'csv'}
                                        onclick={() => updateField(field.id, { breakAfter: nextBreakAfter(field.breakAfter) })}>
                                        {#if field.breakAfter === 2}
                                        <i class="bi bi-text-paragraph" aria-hidden="true"></i><sup>2</sup>
                                        {:else}
                                        <i class="bi bi-text-wrap" aria-hidden="true"></i>
                                        {/if}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm py-0 px-1 fw-bold {field.bold ? 'btn-warning' : 'btn-outline-secondary'}"
                                        title={msg(m.transcript_export_bold, 'Bold')}
                                        aria-pressed={field.bold}
                                        disabled={!isFieldApplicable(field.id)}
                                        onclick={() => updateField(field.id, { bold: !field.bold })}>
                                        B
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm py-0 px-1 fst-italic {field.italic ? 'btn-warning' : 'btn-outline-secondary'}"
                                        title={msg(m.transcript_export_italic, 'Italic')}
                                        aria-pressed={field.italic}
                                        disabled={!isFieldApplicable(field.id)}
                                        onclick={() => updateField(field.id, { italic: !field.italic })}>
                                        I
                                    </button>
                                </div>
                            </div>
                            {/each}
                        </div>
                    </div>
                    {/if}

                </form>
            </div>
            <div class="modal-footer">
                <div class="me-auto d-flex gap-2">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={showLoadPresetDialog}
                        title={m.transcript_export_load_preset_title()}>
                        <i class="bi bi-folder-open" aria-hidden="true"></i> {m.transcript_export_load_preset_button()}
                    </button>
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={saveAsPreset}
                        disabled={!eventGroup}
                        title={m.transcript_export_save_preset_title()}>
                        <i class="bi bi-save" aria-hidden="true"></i> {m.transcript_export_save_preset_button()}
                    </button>
                </div>
                <button 
                    class="btn btn-primary" 
                    type="button" 
                    onclick={exportTranscription} 
                    disabled={!eventGroup}
                    id="exportBtn"
                    aria-disabled={!eventGroup}>
                    {m.transcript_export_export_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#advancedOptions {
    overflow-y: auto;
    overflow-x: none;
    max-height: 50vh;
}
.field-layout-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.field-layout-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.25rem 0.35rem;
    border-radius: 0.25rem;
    background: rgba(0, 0, 0, 0.15);
    cursor: grab;
}
.field-layout-row:active {
    cursor: grabbing;
}
.field-layout-row-muted {
    opacity: 0.45;
}
.drag-handle {
    width: 1rem;
    display: inline-flex;
    justify-content: center;
    color: var(--bs-secondary-color, #6c757d);
}
.field-label {
    min-width: 0;
    flex: 1;
}
.field-controls {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    flex-shrink: 0;
}
.wrap-select {
    width: auto;
    min-width: 5.5rem;
}
.break-btn sup {
    font-size: 0.6rem;
}
</style>
